import React from 'react';
import { ArrowLeftRight, Download, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatAmountInput, formatRial, parseAmountDigits } from '@/lib/formatMoney';
import { amountToPersianWordsToman } from '@/lib/rialWordsFa';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbTransferMenuItem,
  type BsiIbTransferPreview,
} from '@/lib/banks/bsi/api';
import { downloadBsiReceipt } from '@/lib/banks/bsi/download';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';
import { clearBsiTransferPrefill, readBsiTransferPrefill } from '@/lib/api/advances';
import { resolveBsiDestinationAccount } from '@/lib/bsiTransferMode';
import BsiSystemShebaPicker from './BsiSystemShebaPicker';
import BsiPendingSignBanner, { usePendingSignSummary } from './BsiPendingSignBanner';
import BsiTransferAwaitingSign from './BsiTransferAwaitingSign';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

type Step = 'form' | 'confirm' | 'done';
type ChallengeType = 'sms' | 'signature_password' | string;

const BsiTransferTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [mode, setMode] = React.useState<'own_to_other' | 'own_to_own'>('own_to_other');
  const [step, setStep] = React.useState<Step>('form');
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [previewing, setPreviewing] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  const [menu, setMenu] = React.useState<BsiIbTransferMenuItem[]>([]);
  const [formPath, setFormPath] = React.useState<string | null>(null);
  const [draftKey, setDraftKey] = React.useState<string | null>(null);
  const [sources, setSources] = React.useState<BsiIbAccountOption[]>([]);
  const [destinations, setDestinations] = React.useState<BsiIbAccountOption[]>([]);
  const [currencies, setCurrencies] = React.useState<BsiIbAccountOption[]>([]);

  const [sourceAccount, setSourceAccount] = React.useState('');
  const [destinationAccount, setDestinationAccount] = React.useState('');
  const [manualDest, setManualDest] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const [currency, setCurrency] = React.useState('RIAL');
  const [description, setDescription] = React.useState('');
  const [reasonCodes, setReasonCodes] = React.useState<BsiIbAccountOption[]>([]);
  const [reasonCode, setReasonCode] = React.useState('');
  const [reasonDesc, setReasonDesc] = React.useState('');

  const [confirmDraftKey, setConfirmDraftKey] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<BsiIbTransferPreview | null>(null);
  const [challengeType, setChallengeType] = React.useState<ChallengeType>('sms');
  const [challengeLabel, setChallengeLabel] = React.useState('شناسه تأیید پیامک');
  const [authStep, setAuthStep] = React.useState(1);
  const [amountLocked, setAmountLocked] = React.useState(false);
  const [challenge, setChallenge] = React.useState('');
  const [confirmAmount, setConfirmAmount] = React.useState('');
  const [ackIrreversible, setAckIrreversible] = React.useState(false);
  const [reference, setReference] = React.useState<string | null>(null);
  const [receiptKey, setReceiptKey] = React.useState<string | null>(null);
  const [awaitingSign, setAwaitingSign] = React.useState(false);
  const [doneSignableCount, setDoneSignableCount] = React.useState(0);
  const pendingBeforeRef = React.useRef(0);
  const { signableCount, refresh: refreshPending } = usePendingSignSummary(sessionValid);

  const amountDigits = parseAmountDigits(amount);
  const amountTomanWords = amountDigits
    ? amountToPersianWordsToman(Number(amountDigits))
    : '';

  const loadForm = React.useCallback(async (nextMode?: 'own_to_other' | 'own_to_own') => {
    const useMode = nextMode || mode;
    setLoadingForm(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localTransferForm(useMode);
      setMode(res.data.mode || useMode);
      setMenu(res.data.menu || []);
      setFormPath(res.data.form_path);
      setDraftKey(res.data.draft_key);
      setSources(res.data.source_accounts || []);
      setDestinations(res.data.destination_accounts || []);
      setCurrencies(res.data.currencies || []);
      setReasonCodes(res.data.reason_codes || []);
      setSourceAccount(res.data.source_accounts?.[0]?.value || '');
      setDestinationAccount(res.data.destination_accounts?.[0]?.value || '');
      setManualDest(!(res.data.destination_accounts?.length));
      setCurrency(res.data.currencies?.[0]?.value || 'RIAL');
      const codes = res.data.reason_codes || [];
      setReasonCode(codes.find((r) => r.value === 'GPAC')?.value || codes[0]?.value || 'GPAC');
      setReasonDesc('');
      setStep('form');
      setPreview(null);
      setConfirmDraftKey(null);
      setChallenge('');
      setConfirmAmount('');
      setAckIrreversible(false);
      setReference(null);
      setAuthStep(1);
      setAmountLocked(false);
      setChallengeType('sms');
      onMessage({ type: 'success', text: res.message || 'فرم انتقال آماده است.' });
      const prefill = readBsiTransferPrefill();
      if (prefill) {
        setAmount(String(prefill.amount ?? ''));
        if (prefill.description) setDescription(prefill.description);
        const destValue =
          prefill.destination_bsi_account ||
          resolveBsiDestinationAccount({
            iban: prefill.destination_iban,
            account_number: prefill.destination_account,
            card_number: prefill.destination_card,
            bank: prefill.destination_bank_name ? { name: prefill.destination_bank_name } : null,
            suggested_bsi_mode: prefill.suggested_bsi_mode,
          });
        if (destValue) {
          setManualDest(true);
          setDestinationAccount(destValue.replace(/\s+/g, ''));
        }
        clearBsiTransferPrefill();
      }
    } catch (e) {
      const text = e instanceof Error ? e.message : 'خطا در باز کردن فرم انتقال';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoadingForm(false);
    }
  }, [mode, onMessage, onSessionExpired]);

  React.useEffect(() => {
    if (sessionValid && sources.length === 0 && !loadingForm) void loadForm('own_to_other');
  }, [sessionValid, sources.length, loadingForm, loadForm]);

  const handlePreview = async () => {
    if (!sourceAccount || !destinationAccount) {
      onMessage({ type: 'error', text: 'حساب مبدأ و مقصد را انتخاب کنید.' });
      return;
    }
    if (!amountDigits) {
      onMessage({ type: 'error', text: 'مبلغ را وارد کنید.' });
      return;
    }
    if (mode === 'own_to_other' && !reasonCode) {
      onMessage({ type: 'error', text: 'علت انتقال وجه را انتخاب کنید.' });
      return;
    }
    setPreviewing(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localTransferPreview({
        source_account: sourceAccount,
        destination_account: destinationAccount,
        amount: amountDigits,
        description: description.trim() || undefined,
        reason_code: reasonCode || undefined,
        reason_desc: reasonDesc.trim() || description.trim().slice(0, 25) || undefined,
        currency: currency || undefined,
        draft_key: draftKey || undefined,
        mode,
      });
      setPreview(res.data.preview);
      setChallengeType(res.data.challenge_type || 'sms');
      setChallengeLabel(res.data.challenge_label || 'شناسه تأیید پیامک');
      setConfirmDraftKey(res.data.confirm_draft_key);
      setAuthStep(res.data.step || 1);
      setAmountLocked(false);
      setChallenge('');
      setConfirmAmount(formatAmountInput(String(res.data.preview?.amount ?? amountDigits)));
      setStep('confirm');
      onMessage({ type: 'success', text: res.message || 'پیش‌نمایش آماده است.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'پیش‌نمایش انتقال ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmDraftKey) {
      onMessage({ type: 'error', text: 'پیش‌نویس تأیید موجود نیست؛ دوباره پیش‌نمایش بگیرید.' });
      return;
    }
    if (!ackIrreversible) {
      onMessage({ type: 'error', text: 'برای ادامه باید برگشت‌ناپذیر بودن انتقال را تأیید کنید.' });
      return;
    }
    if (!challenge.trim()) {
      onMessage({
        type: 'error',
        text:
          challengeType === 'signature_password'
            ? 'رمز امضا را وارد کنید.'
            : 'شناسه تأیید پیامک را وارد کنید.',
      });
      return;
    }
    if (!amountLocked && (!confirmAmount.trim() || !parseAmountDigits(confirmAmount))) {
      onMessage({ type: 'error', text: 'مبلغ تأیید را دوباره وارد کنید.' });
      return;
    }
    setConfirming(true);
    onMessage(null);
    pendingBeforeRef.current = signableCount;
    try {
      const res = await bsiInternetBankApi.localTransferConfirm({
        confirm_draft_key: confirmDraftKey,
        challenge: challenge.trim(),
        confirm_amount: amountLocked ? undefined : parseAmountDigits(confirmAmount),
      });
      if (res.data.needs_next_step) {
        setConfirmDraftKey(res.data.confirm_draft_key);
        setChallengeType(res.data.challenge_type || 'sms');
        setChallengeLabel(res.data.challenge_label || 'کد تأیید');
        setAuthStep(res.data.step || authStep + 1);
        setAmountLocked(true);
        setChallenge('');
        onMessage({ type: 'success', text: res.message || 'مرحله بعد را تکمیل کنید.' });
        return;
      }
      const refreshed = await refreshPending();
      const count = res.data.signable_count ?? refreshed ?? signableCount;
      const needsSign =
        Boolean(res.data.needs_pending_sign) ||
        count > pendingBeforeRef.current ||
        (!res.data.reference && count > 0);

      setReference(res.data.reference);
      setReceiptKey(res.data.receipt_key);
      setDoneSignableCount(count);
      setAwaitingSign(needsSign);
      setStep('done');
      onMessage({
        type: 'success',
        text: needsSign
          ? res.message || 'انتقال ثبت شد و در انتظار امضاست.'
          : res.message || 'انتقال ثبت شد.',
      });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'تأیید انتقال ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ArrowLeftRight className="h-4 w-4" />
            {mode === 'own_to_own' ? 'انتقال بین حساب‌های خود' : 'انتقال صادراتی (به دیگران)'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sessionValid ? (
            <p className="text-sm text-amber-700">برای انتقال وجه، ابتدا وارد اینترنت‌بانک شوید.</p>
          ) : (
            <>
              {sessionValid ? <BsiPendingSignBanner signableCount={signableCount} className="mb-1" /> : null}
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <div className="mb-1 flex items-center gap-1 font-medium">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  هشدار امنیتی
                </div>
                انتقال وجه برگشت‌ناپذیر است. ابتدا فقط «پیش‌نمایش» بزنید؛ تأیید نهایی فقط با OTP و وارد کردن دوباره مبلغ انجام می‌شود.
              </div>

              {mode === 'own_to_other' && (
                <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                  <p className="mb-1 font-medium">سقف‌های انتقال صادراتی (بانک)</p>
                  <ul className="list-disc space-y-0.5 pr-4">
                    <li>سقف هر تراکنش: {formatRial(2_000_000_000)} ریال</li>
                    <li>سقف روزانه از هر حساب: {formatRial(10_000_000_000)} ریال</li>
                    <li>کارمزد هر تراکنش: {formatRial(450)} ریال</li>
                  </ul>
                </div>
              )}

              {formPath && (
                <p className="text-xs text-slate-500">
                  مسیر فرم بانک: <span className="font-mono">{formPath}</span>
                </p>
              )}

              {step === 'form' && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label>نوع انتقال</Label>
                    <SearchableSelect
                      value={mode}
                      onChange={(v) => {
                        const next = v === 'own_to_own' ? 'own_to_own' : 'own_to_other';
                        setMode(next);
                        void loadForm(next);
                      }}
                      options={[
                        { value: 'own_to_other', label: 'به حساب دیگران (صادراتی)' },
                        { value: 'own_to_own', label: 'بین حساب‌های خودم' },
                      ]}
                      placeholder="نوع"
                      disabled={loadingForm}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>حساب مبدأ</Label>
                    {sources.length > 0 ? (
                      <SearchableSelect
                        value={sourceAccount}
                        onChange={setSourceAccount}
                        options={sources.map((a) => ({ value: a.value, label: a.label || a.value }))}
                        placeholder={loadingForm ? 'در حال دریافت…' : 'انتخاب مبدأ'}
                        disabled={loadingForm}
                      />
                    ) : (
                      <Input
                        value={sourceAccount}
                        onChange={(e) => setSourceAccount(e.target.value)}
                        placeholder="شماره حساب مبدأ"
                        disabled={loadingForm}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label>حساب مقصد</Label>
                      {mode === 'own_to_other' && destinations.length > 0 && (
                        <button
                          type="button"
                          className="text-xs text-slate-600 underline"
                          onClick={() => {
                            setManualDest((v) => !v);
                            if (!manualDest) setDestinationAccount('');
                          }}
                        >
                          {manualDest ? 'انتخاب از لیست' : 'ورود دستی'}
                        </button>
                      )}
                    </div>
                    {!manualDest && destinations.length > 0 ? (
                      <SearchableSelect
                        value={destinationAccount}
                        onChange={setDestinationAccount}
                        options={destinations.map((a) => ({ value: a.value, label: a.label || a.value }))}
                        placeholder={loadingForm ? 'در حال دریافت…' : 'انتخاب مقصد'}
                        disabled={loadingForm}
                      />
                    ) : (
                      <Input
                        value={destinationAccount}
                        onChange={(e) => setDestinationAccount(e.target.value)}
                        placeholder="شماره حساب مقصد"
                        disabled={loadingForm}
                      />
                    )}
                  </div>
                  {mode === 'own_to_other' && (
                    <BsiSystemShebaPicker
                      mode="account"
                      disabled={loadingForm}
                      onSelect={(selection) => {
                        setManualDest(true);
                        setDestinationAccount(selection.accountNumber);
                        if (selection.employeeLabel && !description.trim()) {
                          setDescription(selection.employeeLabel);
                        }
                        if (selection.employeeLabel && !reasonDesc.trim()) {
                          setReasonDesc(selection.employeeLabel.slice(0, 25));
                        }
                      }}
                    />
                  )}
                  <div className="space-y-2">
                    <Label>مبلغ (ریال)</Label>
                    <Input
                      value={amount}
                      onChange={(e) => setAmount(formatAmountInput(e.target.value))}
                      inputMode="numeric"
                      placeholder="مثلاً 1,000,000"
                      className="font-mono"
                      dir="ltr"
                    />
                    {amountTomanWords ? (
                      <p className="text-xs text-slate-600">{amountTomanWords}</p>
                    ) : (
                      <p className="text-xs text-slate-400">معادل به حروف (تومان) اینجا نمایش داده می‌شود</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>ارز</Label>
                    <SearchableSelect
                      value={currency}
                      onChange={setCurrency}
                      options={
                        currencies.length
                          ? currencies.map((c) => ({ value: c.value, label: c.label || c.value }))
                          : [{ value: 'RIAL', label: 'ریال ایران' }]
                      }
                      placeholder="ارز"
                    />
                  </div>
                  {mode === 'own_to_other' && (
                    <div className="space-y-2 md:col-span-2">
                      <Label>علت انتقال وجه</Label>
                      <SearchableSelect
                        value={reasonCode}
                        onChange={setReasonCode}
                        options={
                          reasonCodes.length
                            ? reasonCodes.map((r) => ({ value: r.value, label: r.label || r.value }))
                            : [
                                { value: 'GPAC', label: 'هزینه عمومی و امور روزمره' },
                                { value: 'GPPC', label: 'خرید کالا' },
                                { value: 'SPAC', label: 'خرید خدمات' },
                              ]
                        }
                        placeholder="انتخاب علت"
                        disabled={loadingForm}
                      />
                    </div>
                  )}
                  <div className="space-y-2 md:col-span-2">
                    <Label>شرح علت انتقال {mode === 'own_to_other' ? '' : '(اختیاری)'}</Label>
                    <Input
                      value={reasonDesc}
                      onChange={(e) => setReasonDesc(e.target.value.slice(0, 25))}
                      placeholder="حداکثر ۲۵ کاراکتر"
                      maxLength={25}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>شرح (اختیاری)</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="بابت …"
                      maxLength={200}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 md:col-span-2">
                    <Button type="button" variant="outline" onClick={() => void loadForm(mode)} disabled={loadingForm}>
                      {loadingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      <span className="mr-1">بروزرسانی فرم</span>
                    </Button>
                    <Button type="button" onClick={() => void handlePreview()} disabled={previewing || loadingForm}>
                      {previewing ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                      پیش‌نمایش انتقال
                    </Button>
                  </div>
                </div>
              )}

              {step === 'confirm' && preview && (
                <div className="space-y-4">
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    مرحله {authStep} از تأیید بانک
                    {challengeType === 'signature_password'
                      ? ' — رمز امضا'
                      : ' — شناسه تأیید پیامک'}
                  </div>
                  <div className="rounded-md border bg-slate-50 px-3 py-3 text-sm space-y-1">
                    <p>
                      از: <span className="font-mono">{preview.source_account}</span>
                    </p>
                    <p>
                      به: <span className="font-mono">{preview.destination_account}</span>
                    </p>
                    <p>
                      مبلغ: <span className="font-mono font-semibold">{formatRial(preview.amount)}</span> ریال
                    </p>
                    <p className="text-xs text-slate-600">{amountToPersianWordsToman(preview.amount)}</p>
                    {preview.reason_code && (
                      <p>
                        علت: {preview.reason_code}
                        {preview.reason_desc ? ` — ${preview.reason_desc}` : ''}
                      </p>
                    )}
                    {preview.description && <p>شرح: {preview.description}</p>}
                  </div>

                  {!amountLocked && (
                    <div className="space-y-2">
                      <Label>تأیید مبلغ (دوباره وارد کنید)</Label>
                      <Input
                        value={confirmAmount}
                        onChange={(e) => setConfirmAmount(formatAmountInput(e.target.value))}
                        inputMode="numeric"
                        className="font-mono"
                        dir="ltr"
                        placeholder="مثلاً 1,000,000"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>{challengeLabel}</Label>
                    <Input
                      type={challengeType === 'signature_password' ? 'password' : 'text'}
                      value={challenge}
                      onChange={(e) => setChallenge(e.target.value)}
                      inputMode={challengeType === 'sms' ? 'numeric' : undefined}
                      autoComplete={challengeType === 'sms' ? 'one-time-code' : 'current-password'}
                      placeholder={
                        challengeType === 'signature_password'
                          ? 'رمز امضای اینترنت‌بانک'
                          : 'کد دریافتی از پیامک بانک'
                      }
                    />
                    <p className="text-xs text-slate-500">
                      {challengeType === 'signature_password'
                        ? 'پس از وارد کردن رمز امضا، ممکن است پیامک تأیید دیگری ارسال شود.'
                        : 'پیامک بانک را چک کنید؛ اعتبار شناسه محدود است.'}
                    </p>
                  </div>

                  <label className="flex items-start gap-2 text-sm text-rose-800">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={ackIrreversible}
                      onChange={(e) => setAckIrreversible(e.target.checked)}
                    />
                    <span>می‌دانم این انتقال برگشت‌ناپذیر است و جزئیات را بررسی کرده‌ام.</span>
                  </label>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStep('form');
                        setPreview(null);
                        setConfirmDraftKey(null);
                        setChallenge('');
                        setAmountLocked(false);
                      }}
                      disabled={confirming}
                    >
                      بازگشت
                    </Button>
                    <Button type="button" variant="destructive" onClick={() => void handleConfirm()} disabled={confirming}>
                      {confirming ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                      {challengeType === 'signature_password' ? 'ارسال رمز امضا' : 'تأیید با پیامک'}
                    </Button>
                  </div>
                </div>
              )}

              {step === 'done' && awaitingSign && (
                <BsiTransferAwaitingSign
                  reference={reference}
                  signableCount={doneSignableCount || signableCount}
                  onNewTransfer={() => {
                    setStep('form');
                    setAwaitingSign(false);
                    setAmount('');
                    setDescription('');
                    setPreview(null);
                    setReference(null);
                    setReceiptKey(null);
                  }}
                />
              )}

              {step === 'done' && !awaitingSign && (
                <div className="space-y-3 text-sm">
                  <p className="text-emerald-700">درخواست انتقال ارسال شد.</p>
                  {reference && (
                    <p>
                      شماره پیگیری: <span className="font-mono font-semibold">{reference}</span>
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {receiptKey && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            void downloadBsiReceipt(receiptKey, 'html').catch((e) =>
                              onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                            )
                          }
                        >
                          <Download className="ml-1 h-4 w-4" />
                          HTML
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            void downloadBsiReceipt(receiptKey, 'pdf').catch((e) =>
                              onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                            )
                          }
                        >
                          <Download className="ml-1 h-4 w-4" />
                          PDF
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            void downloadBsiReceipt(receiptKey, 'excel').catch((e) =>
                              onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                            )
                          }
                        >
                          <Download className="ml-1 h-4 w-4" />
                          اکسل
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStep('form');
                        setAwaitingSign(false);
                        setAmount('');
                        setDescription('');
                        setPreview(null);
                        setReference(null);
                        setReceiptKey(null);
                      }}
                    >
                      انتقال جدید
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {menu.length > 0 && (
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">منوی انتقال کشف‌شده از بانک</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {menu.map((m) => (
                  <li key={m.path} className="flex flex-wrap gap-2">
                    <span>{m.label}</span>
                    <span className="font-mono text-xs text-slate-500">{m.path}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default BsiTransferTab;
